// Types
import type { TVBetweenOption } from '~/misc/v-between/types';
import type { IVDropdownOption } from '~/misc/v-dropdown/types';
import type {
  TRangeLimitStep,
  TListingsPriceOptions,
  TListingSaleTypeOption,
  TSearchStatus,
  TListingStatusCount,
} from '~/misc/listings/types';
import {
  EPropertyCategory,
  EDateFilterOptions,
  ESaleType,
  EListingStatus,
  ESearchRequestStatusName,
  EHiddenFilter,
} from '~/misc/listings/types';

// Utils
import { formatPrice } from '~/misc/utils/number';

const BASE_LISTING_CONTEXT_INJECTION_SYMBOL: unique symbol = Symbol('base-listing-context');
const REFETCH_LISTINGS_INJECTION_SYMBOL: unique symbol = Symbol('refetch-listings');
const REFETCH_LISTINGS_TOTALS_INJECTION_SYMBOL: unique symbol = Symbol('refetch-listings-totals');
const REFETCH_OPPORTUNITIES_INJECTION_SYMBOL: unique symbol = Symbol('refetch-opportunities');
const REFETCH_AFTER_CTA_SUBMIT_INJECTION_SYMBOL: unique symbol = Symbol('refetch-after-cta-submit');

const LISTINGS_BEDS_OPTIONS: TVBetweenOption<string, number>[] = [
  {
    label: 'Studio',
    value: 0,
  },
  {
    label: '1 bed',
    value: 1,
  },
  {
    label: '2 beds',
    value: 2,
  },
  {
    label: '3 beds',
    value: 3,
  },
  {
    label: '4 beds',
    value: 4,
  },
  {
    label: '5 beds',
    value: 5,
  },
];

const LISTINGS_TYPE_OPTIONS: IVDropdownOption<string, EPropertyCategory | null>[] = [
  {
    label: 'Any',
    value: null,
  },
  {
    label: 'House',
    value: EPropertyCategory.HOUSE,
  },
  {
    label: 'Flat',
    value: EPropertyCategory.FLAT,
  },
];

const LISTINGS_DATE_OPTIONS: IVDropdownOption<string, EDateFilterOptions | null>[] = [
  {
    label: 'Any',
    value: null,
  },
  {
    label: '3 days',
    value: EDateFilterOptions._3_DAYS,
  },
  {
    label: '7 days',
    value: EDateFilterOptions._7_DAYS,
  },
  {
    label: '14 days',
    value: EDateFilterOptions._14_DAYS,
  },
  {
    label: '28 days',
    value: EDateFilterOptions._28_DAYS,
  },
  {
    label: '2 months',
    value: EDateFilterOptions._2_MONTHS,
  },
  {
    label: '6 months',
    value: EDateFilterOptions._6_MONTHS,
  },
];

const HIDDEN_FILTER_OPTIONS: IVDropdownOption<string, EHiddenFilter>[] = [
  {
    label: "Don't show",
    value: EHiddenFilter.DONT_SHOW,
  },
  {
    label: 'Show everything',
    value: EHiddenFilter.SHOW_ALL,
  },
  {
    label: 'Show only hidden',
    value: EHiddenFilter.SHOW_ONLY_HIDDEN,
  },
];

const LISTINGS_SALE_TYPE_OPTIONS: TListingSaleTypeOption[] = [
  { label: 'Sales', value: ESaleType.SALES },
  { label: 'Lettings', value: ESaleType.LETTINGS },
];

const SALE_RANGE_LIMIT_STEP: TRangeLimitStep[] = [
  {
    from: 10 * 1000,
    step: 10 * 1000,
  },
  {
    from: 250 * 1000,
    step: 25 * 1000,
  },
  {
    from: 500 * 1000,
    step: 50 * 1000,
  },
  {
    from: 1000 * 1000,
    step: 100 * 1000,
  },
  {
    from: 2500 * 1000,
    step: 250 * 1000,
  },
  {
    from: 5000 * 1000,
    step: 500 * 1000,
  },
  {
    from: 10000 * 1000,
    step: 2500 * 1000,
  },
  {
    from: 15000 * 1000,
  },
];

const LET_RANGE_LIMIT_STEP: TRangeLimitStep[] = [
  {
    from: 100,
    step: 10,
  },
  {
    from: 250,
    step: 25,
  },
  {
    from: 500,
    step: 50,
  },
  {
    from: 1000,
    step: 100,
  },
  {
    from: 2500,
    step: 250,
  },
  {
    from: 5000,
    step: 500,
  },
  {
    from: 10000,
    step: 2500,
  },
  {
    from: 15000,
  },
];

const generatePriceOptions = (steps: TRangeLimitStep[], format?: string): TListingsPriceOptions =>
  steps
    .map(
      (
        rangeLimit: {
          from: number;
          step?: number;
        },
        index: number
      ): TListingsPriceOptions => {
        const from: number = rangeLimit.from;
        const to: number | undefined = steps[index + 1]?.from;

        const step: number | undefined = rangeLimit.step;

        if (typeof to === 'number' && typeof step === 'number') {
          const ranges: TListingsPriceOptions = [];

          const rangesTotal: number = (to - from) / step;

          for (let i = 0; i < rangesTotal; i++) {
            const rangeFrom: number = from + step * i;

            ranges.push({
              label: formatPrice(rangeFrom, format),
              value: rangeFrom,
            });
          }

          return ranges;
        }

        return [
          {
            label: formatPrice(from, format),
            value: from,
          },
        ];
      }
    )
    .flat();

const SALE_LISTINGS_PRICE_OPTIONS: TListingsPriceOptions =
  generatePriceOptions(SALE_RANGE_LIMIT_STEP);

const LET_LISTINGS_PRICE_OPTIONS: TListingsPriceOptions =
  generatePriceOptions(LET_RANGE_LIMIT_STEP);

const SHORTEN_SALE_LISTINGS_PRICE_OPTIONS: TListingsPriceOptions = generatePriceOptions(
  SALE_RANGE_LIMIT_STEP,
  '0.[0]a'
);

const LISTING_STATUS_MAP: Record<EListingStatus, TSearchStatus> = {
  [EListingStatus.NEW]: {
    label: 'New',
    request: {
      name: ESearchRequestStatusName.NEW,
    },
    can_filter_agent: true,
  },
  [EListingStatus.NEW_ON_MARKET]: {
    label: 'New',
    request: {
      name: ESearchRequestStatusName.NEW_ON_MARKET,
    },
    can_filter_agent: true,
  },
  [EListingStatus.UNDER_OFFER]: {
    label: 'Under Offer',
    request: {
      name: ESearchRequestStatusName.UNDER_OFFER,
    },
    can_filter_agent: true,
  },
  [EListingStatus.SSTC]: {
    label: 'SSTC',
    request: {
      name: ESearchRequestStatusName.SSTC,
    },
    can_filter_agent: true,
  },
  [EListingStatus.LIKELY_SOLD]: {
    label: 'Likely Sold',
    request: {
      name: ESearchRequestStatusName.LIKELY_SOLD,
    },
    can_filter_agent: true,
  },
  [EListingStatus.RECENTLY_SOLD]: {
    label: 'Recently Sold',
    request: {
      name: ESearchRequestStatusName.RECENTLY_SOLD,
    },
  },
  [EListingStatus.JUST_VALUED]: {
    label: 'We Just valued',
    request: {
      name: ESearchRequestStatusName.JUST_VALUED,
    },
    can_filter_agent: true,
  },
  [EListingStatus.REDUCED]: {
    label: 'Reduced',
    request: {
      name: ESearchRequestStatusName.REDUCED,
    },
    can_filter_agent: true,
  },
  [EListingStatus.FALLEN_THROUGH]: {
    label: 'Fallen through',
    request: {
      name: ESearchRequestStatusName.FALLEN_THROUGH,
    },
    can_filter_agent: true,
  },
  [EListingStatus.WITHDRAWN]: {
    label: 'Withdrawn',
    request: {
      name: ESearchRequestStatusName.WITHDRAWN,
    },
    can_filter_agent: true,
  },
  [EListingStatus.OVER_8_WEEKS]: {
    label: '8+ weeks',
    request: {
      name: ESearchRequestStatusName.OVER_WEEKS,
      params: { weeks: 8, weeks_to: 12 },
    },
    can_filter_agent: true,
  },
  [EListingStatus.OVER_12_WEEKS]: {
    label: '12+ weeks',
    request: {
      name: ESearchRequestStatusName.OVER_WEEKS,
      params: { weeks: 12, weeks_to: 16 },
    },
    can_filter_agent: true,
  },
  [EListingStatus.OVER_16_WEEKS]: {
    label: '16+ weeks',
    request: {
      name: ESearchRequestStatusName.OVER_WEEKS,
      params: { weeks: 16 },
    },
    can_filter_agent: true,
  },
  [EListingStatus.OVER_2_WEEKS]: {
    label: 'Slow movers (2 Weeks +)',
    request: {
      name: ESearchRequestStatusName.OVER_WEEKS,
      params: { weeks: 2 },
    },
    can_filter_agent: true,
  },
  [EListingStatus.LET_AGREED]: {
    label: 'Let agreed',
    request: {
      name: ESearchRequestStatusName.LET_AGREED,
    },
    can_filter_agent: true,
  },
  [EListingStatus.LIKELY_LET]: {
    label: 'Likely let',
    request: {
      name: ESearchRequestStatusName.LIKELY_LET,
    },
    can_filter_agent: true,
  },
  [EListingStatus.ANNIVERSARY_1_YEAR]: {
    label: '1 Yr',
    request: {
      name: ESearchRequestStatusName.ANNIVERSARY,
      params: { years: 1 },
    },
  },
  [EListingStatus.ANNIVERSARY_2_YEARS]: {
    label: '2 Yrs',
    request: {
      name: ESearchRequestStatusName.ANNIVERSARY,
      params: { years: 2 },
    },
  },
  [EListingStatus.ANNIVERSARY_3_YEARS]: {
    label: '3 Yrs',
    request: {
      name: ESearchRequestStatusName.ANNIVERSARY,
      params: { years: 3 },
    },
  },
  [EListingStatus.ANNIVERSARY_5_YEARS]: {
    label: '5 Yrs',
    request: {
      name: ESearchRequestStatusName.ANNIVERSARY,
      params: { years: 5 },
    },
  },
  [EListingStatus.ANNIVERSARY_7_YEARS]: {
    label: '7 Yrs',
    request: {
      name: ESearchRequestStatusName.ANNIVERSARY,
      params: { years: 7 },
    },
  },
  [EListingStatus.ANNIVERSARY_OVER_10_YEARS]: {
    label: '10 Yrs+',
    request: {
      name: ESearchRequestStatusName.ANNIVERSARY,
      params: { years: 10 },
    },
  },
  [EListingStatus.UNDER_OFFER_AND_SSTC]: {
    label: 'Under Offer / SSTC',
    request: {
      name: ESearchRequestStatusName.UNDER_OFFER_AND_SSTC,
    },
    date_label: 'Success',
    can_filter_agent: true,
  },
};

const DEFAULT_LISTING_STATUS_COUNT: TListingStatusCount = Object.keys(EListingStatus).reduce(
  (acc: TListingStatusCount, key: string) => {
    const item: EListingStatus = EListingStatus[key as keyof typeof EListingStatus];

    acc[item] = 0;

    return acc;
  },
  {} as TListingStatusCount
);

export {
  BASE_LISTING_CONTEXT_INJECTION_SYMBOL,
  REFETCH_LISTINGS_INJECTION_SYMBOL,
  REFETCH_LISTINGS_TOTALS_INJECTION_SYMBOL,
  REFETCH_OPPORTUNITIES_INJECTION_SYMBOL,
  REFETCH_AFTER_CTA_SUBMIT_INJECTION_SYMBOL,
  LISTINGS_BEDS_OPTIONS,
  LISTINGS_TYPE_OPTIONS,
  LISTINGS_DATE_OPTIONS,
  HIDDEN_FILTER_OPTIONS,
  SALE_LISTINGS_PRICE_OPTIONS,
  LET_LISTINGS_PRICE_OPTIONS,
  SHORTEN_SALE_LISTINGS_PRICE_OPTIONS,
  LISTINGS_SALE_TYPE_OPTIONS,
  LISTING_STATUS_MAP,
  DEFAULT_LISTING_STATUS_COUNT,
};
